import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Send } from "lucide-react";
import bgImage from "../../Images/login.svg";
import Logo from "../../Images/Logo.svg";
import Lock from "../../Images/lock.svg";
import ToastMessage from "../../config/ToastMessage";
import {API_URL} from "../../config/api";

export default function PasswordLink() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // toast state
  const [toast, setToast] = useState({
    type: "",
    message: "",
  });

  const handleSubmit = async (e) => {
  e.preventDefault();
  setError("");
  setToast({ type: "", message: "" });

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!email) {
    setError("Email is required");
    return;
  }

  if (!emailRegex.test(email)) {
    setError("Please enter a valid email address");
    return;
  }

  setLoading(true);

  try {
    const response = await fetch(`${API_URL}/forgot-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (!response.ok || data.success === false) {
      throw new Error(data.message || "Something went wrong");
    }

    // ✅ Success
    setToast({
      type: "success",
      message: data.message || "Password reset link sent to your email!",
    },2000);
    setEmail("");

  } catch (err) {
    // ❌ Error
    setToast({
      type: "error",
      message: err.message || "Failed to send reset link",
    });
  } finally {
    setLoading(false);
  }
};


  return (
    <div
      className="relative flex min-h-screen items-center justify-end bg-gray-50"
      style={{
        backgroundImage: `url(${bgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Toast */}
      <ToastMessage
        type={toast.type}
        message={toast.message}
        onClose={() => setToast({ type: "", message: "" })}
      />

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/40" />

      {/* White Card Container - Same as Login Page */}
      <div className="relative z-10 bg-white rounded-2xl shadow-[0_12px_24px_rgba(32,33,35,0.1)] border border-zinc-200 w-[90%] max-w-md p-8 flex flex-col gap-6 mr-20">
        {/* Logo */}
        <div className="flex justify-start mb-2">
          <img src={Logo} alt="SkillsMD Logo" className="w-36 h-auto" />
        </div>

        {/* Icon Circle */}
        <div className="w-16 h-16 bg-white rounded-full border-8 border-gray-200 flex items-center justify-center">
          <div className="w-8 h-8 bg-gradient-to-br to-slate-700 rounded-full flex items-center justify-center">
            <img src={Lock} alt="lock_icon" />
          </div>
        </div>

        {/* Title and Description */}
        <div className="text-left">
          <h1 className="text-zinc-800 text-lg font-bold font-['Roboto'] mb-2">
            Forgot Your Password?
          </h1>
          <p className="text-zinc-600 text-sm font-normal font-['Roboto'] leading-5">
            Enter your registered email address, and we will send you a link to
            reset your password
          </p>
        </div>

        {/* Form */}
        <div className="flex flex-col gap-4">
          {/* Email Input */}
          <div className="flex flex-col items-start gap-2">
            <label className="text-sm font-bold text-neutral-800">
              Email <span className="text-red-600">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="h-10 w-full px-3 bg-white rounded-md shadow-[0_0_5px_rgba(32,33,35,0.07)] outline outline-1 outline-zinc-200 text-sm text-zinc-700 focus:outline-[#4AACC5] focus:ring-1 focus:ring-[#4AACC5]"
            />
            {error && (
              <p className="text-red-600 text-xs font-normal font-['Roboto']">
                {error}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`mt-2 w-full px-5 py-2.5 rounded-md text-white text-sm font-bold shadow-sm transition-all flex items-center justify-center gap-2 ${
              loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-[#4AACC5] hover:bg-[#3E95AC]"
            }`}
          >
            <Send className="w-4 h-4 text-white" />
            <span>{loading ? "Sending..." : "Send Reset Link"}</span>
          </button>

          {/* Back to Login Link */}
          <a
            href="/login"
            className="flex items-center justify-center gap-1 text-xs font-normal font-['Roboto'] leading-4 hover:underline mt-2"
          >
            <ArrowLeft className="w-3 h-3 text-neutral-700" />
            <span className="text-neutral-700">Back to </span>
            <span className="text-[#4AACC5] underline">Login</span>
          </a>
        </div>

        {/* Privacy Notice */}
        <div className="self-stretch text-left mt-2">
          <p className="text-zinc-800 text-xs font-normal leading-5">
            By continuing, you agree to{" "}
            <span className="text-[#4AACC5] font-medium cursor-pointer">
              SkillsMD
            </span>{" "}
            Agreement or other agreement for, and the Privacy Notice. This site
            uses essential cookies. See our Cookie Notice for more information.
          </p>
        </div>
      </div>
    </div>
  );
}
